import { useState } from "react";

import { trackRedeemClick } from "@loyalty/utils";

import type { PrizeLocatorContextType, PrizeLocatorProps } from "@des/locators";
import { PrizeLocator } from "@des/locators";

import type { GetPrizesElementResponseType } from "@jti/rest-api-types";
import { useSelector } from "@jti/store/reduxHooks";

import { RedeemDialogV2 } from "../RedeemDialogV2";
import type { SimplifiedPrizesV2Props } from "../SimplifiedPrizes/SimplifiedPrizesV2";

type FiltersTranslations = {
  filterKey: string;
  translatedFilterKey: string;
  image: string;
};

export type TrustedPlacesV2Props = {
  searchHeader?: string;
  searchBar?: string;
  rewardsText?: string;
  searchButton?: string;
  storesCount?: string;
  getDirectionLabel?: string;
  backToListLabel?: string;
  categoryFilterTitle?: string;
  venuePopUpOffersCount?: string;
  currentPositionButtonText?: string;
  filtersTranslations?: FiltersTranslations[];
  logoUrl: string;
  logoAlt: string;
  isInEditor?: boolean;
  prizesPageUrl?: string;
  quantityAriaLabel?: string;
  increaseAriaLabel?: string;
  decreaseAriaLabel?: string;
} & Partial<Omit<PrizeLocatorProps, "activeStoreId" | "context" | "filtersEnabled">>;

/**
 * Component for displaying trusted places with a prize locator and RedeemDialogV2 for builder only.
 **/

export const TrustedPlacesV2 = (
  props: TrustedPlacesV2Props &
    Omit<
      SimplifiedPrizesV2Props,
      | "color"
      | "showNotAvailablePrizes"
      | "confirmationMessage"
      | "confirmationMessagePlural"
      | "isTeaser"
      | "teaserData"
      | "errorMessage"
      | "tierRestrictedLabel"
      | "mapCategoryFilterTitle"
    >
) => {
  const [dialogData, setDialogData] = useState<GetPrizesElementResponseType | Record<string, never>>({});
  const handleRedeemClick = (data: GetPrizesElementResponseType) => {
    setDialogData(data);
    trackRedeemClick(data);
  };
  const {
    points: { balance },
    tier: { name },
  } = useSelector((state) => state.loyaltyCore);

  const {
    pointsLabel,
    ctaLabel,
    loadMoreButtonLabel,
    searchHeader,
    searchBar,
    rewardsText,
    backToListLabel,
    searchButton,
    storesCount,
    getDirectionLabel,
    defaultLng,
    defaultLat,
    flotiqContentType,
    flotiqApiKey,
    filtersTranslations,
    categoryFilterTitle,
    noOffersText,
    pagination,
    venuePopUpOffersCount,
    currentPositionButtonText,
    newOfferDays,
    newLabel,
    oneDayLabel,
    daysLabel,
    outOfStockLabel,
    discoverUrlParent,
    discoverButtonText,
    isInEditor,
    prizesPageUrl,
    logoUrl,
    logoAlt,
    quantityAriaLabel,
    increaseAriaLabel,
    decreaseAriaLabel,
  } = props;

  const context: PrizeLocatorContextType = {
    labels: {
      loadMoreLabel: loadMoreButtonLabel,
      redeemButtonLabel: ctaLabel,
      backToListLabel: backToListLabel ?? "",
      getDirectionLabel: getDirectionLabel ?? "",
      searchHeader: searchHeader ?? "",
      pointsLabel: pointsLabel ?? "",
      rewardsText: rewardsText ?? "",
      searchBar: searchBar ?? "",
      searchButton: searchButton ?? "",
      storesCount: storesCount ?? "",
      categoryFilterTitle: categoryFilterTitle ?? "",
      noOffersText,
      venuePopUpOffersCount: venuePopUpOffersCount ?? "",
      currentPositionButtonText: currentPositionButtonText ?? "",
      newOfferDays,
      newLabel,
      oneDayLabel,
      daysLabel,
      outOfStockLabel,
      discoverButtonText,
    },
    pagination,
    userPointsBalance: balance,
    handleRedeemClick,
    tier: name,
    discoverUrlParent,
  };

  const resetDialogData = () => setDialogData({});

  return (
    <>
      <PrizeLocator
        context={context}
        defaultLat={defaultLat ?? 0}
        defaultLng={defaultLng ?? 0}
        flotiqApiKey={flotiqApiKey ?? ""}
        flotiqContentType={flotiqContentType ?? ""}
        filtersTranslations={filtersTranslations}
      />

      <RedeemDialogV2
        isDialogOpen={!!dialogData?.prize_uuid}
        onCloseDialog={resetDialogData}
        logoAlt={logoAlt}
        logoUrl={logoUrl}
        prizeData={dialogData}
        pointsBalance={balance}
        isInEditor={isInEditor}
        prizesPageUrl={prizesPageUrl}
        quantityAriaLabel={quantityAriaLabel}
        increaseAriaLabel={increaseAriaLabel}
        decreaseAriaLabel={decreaseAriaLabel}
      />
    </>
  );
};
